import React, { FunctionComponent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartAddressForm from "@Components/molecules/cart/CartAddressForm";
import CartDeliveryMethod from "@Components/molecules/cart/CartDeliveryMethod";
import CartMondialRelay from "@Components/molecules/cart/CartMondialRelay";
import CartPostcodeModal from "@Components/molecules/cart/CartPostcodeModal";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { BillingAddress, DeliveryAddress, ShippingOptions } from "@Types/addresses";
import CheerzApiAddresses from "@Types/api/addresses";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse } from "@Types/api/error";
import { Country, Step } from "@Types/cart";
import UserContent from "@Types/user";
import {
  convertFromAddressesContentToApi,
  convertFromApiToAddressesContent,
} from "@Utils/cart/addresses";
import { convertFromApiToCartContent, convertFromCartContentToApi } from "@Utils/cart/cart";
import { convertToCheckoutStepProperties, setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  countries: Country[];
  disabled: boolean;
  onSubmitAddress: ({
    billing,
    delivery,
  }: {
    billing: Partial<BillingAddress> | null;
    delivery: Partial<DeliveryAddress>;
  }) => void;
  onConfirmZipCodeModal: () => void;
  onDismissZipCodeModal: () => void;
  shippingOptions: { home: ShippingOptions; mondialRelay?: ShippingOptions };
  showZipCodeModal: boolean;
  postcode: string | undefined;
  user: UserContent;
}

interface ContainerProps {
  disabled: boolean;
  onChangeStep: (step: Step) => void;
  onValidatePostcode: (
    postcode: string | undefined,
    countryTag: string | undefined
  ) => Promise<boolean>;
}

const CartAddressStep: FunctionComponent<Props> = ({
  countries,
  disabled,
  onSubmitAddress,
  onConfirmZipCodeModal,
  onDismissZipCodeModal,
  shippingOptions,
  showZipCodeModal,
  postcode,
  user,
}) => {
  const intl = useIntl();
  const localeSlug = intl.locale;
  const callbackUrl = encodeURIComponent(`/${localeSlug}/cart`);
  const loginUrl = `/${localeSlug}/users/sign_in?callback_url=${callbackUrl}`;

  return (
    <div className="CartAddressStep">
      {user.isGuest && (
        <a href={loginUrl} className="CartAddressStep-login">
          <FormattedMessage id={"cheerz_CartAddressStep_login"} />
        </a>
      )}
      <CartDeliveryMethod />
      {(shippingOptions.home.normal.selected || shippingOptions.home.express.selected) && (
        <CartAddressForm disabled={disabled} countries={countries} onSubmit={onSubmitAddress} />
      )}
      <CartPostcodeModal
        display={showZipCodeModal}
        onConfirm={onConfirmZipCodeModal}
        onDismiss={onDismissZipCodeModal}
        postcode={postcode ? postcode : ""}
      />

      {shippingOptions.mondialRelay &&
        (shippingOptions.mondialRelay.normal.selected ||
          shippingOptions.mondialRelay.express.selected) && (
          <CartMondialRelay disabled={disabled} onSubmitAddress={onSubmitAddress} />
        )}
    </div>
  );
};

export { CartAddressStep };

const CartAddressStepContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  onChangeStep,
  onValidatePostcode,
}) => {
  const { addresses, cart, configuration, dispatch, user } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();
  const [postcodeWarningAddresses, setPostcodeWarningAddresses] = useState<{
    billing: Partial<BillingAddress> | null;
    delivery: Partial<DeliveryAddress>;
  }>(addresses);
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);

  useEffect(() => {
    setDataLayerTrack("Checkout Step Viewed", convertToCheckoutStepProperties("Address", 2, cart));
  }, []);

  const handleSubmitAddress = (
    {
      billing,
      delivery,
    }: {
      billing: Partial<BillingAddress> | null;
      delivery: Partial<DeliveryAddress>;
    },
    confirmed = false
  ) => {
    setPostcodeWarningAddresses({
      billing,
      delivery,
    });
    onValidatePostcode(delivery.postcode, delivery.countryTag2)
      .then((isValidPostcode: boolean) => {
        if (isValidPostcode === false && confirmed === false) {
          setShowZipCodeModal(true);
          return;
        }
        if (configuration) {
          dispatch({
            disabled: true,
            type: "setDisabledCart",
          });

          configuration.apiProviders.cheerz
            .saveAddresses(convertFromAddressesContentToApi(billing, delivery))
            .then((addressesResponse: CheerzApiAddresses) => {
              dispatch({
                addresses: {
                  ...convertFromApiToAddressesContent(addressesResponse),
                },
                type: "setSavedAddresses",
              });

              const updatedCartJson = convertFromCartContentToApi(cart);

              configuration.apiProviders.cheerz
                .saveCart(updatedCartJson)
                .then((cartResponse: CheerzApiCart) => {
                  dispatch({
                    cart: {
                      ...convertFromApiToCartContent(cartResponse),
                    },
                    type: "setSavedCart",
                  });
                  dispatch({
                    disabled: false,
                    type: "setDisabledCart",
                  });
                  onChangeStep("overview");
                })
                .catch(() => {
                  return;
                });
            })
            .catch((error: ApiErrorResponse) => {
              const errorDescription = error.response?.data?.error_description ?? undefined;
              dispatch({
                disabled: false,
                type: "setDisabledCart",
              });
              setDataLayerTrack("Address Error Occured", {
                error: errorDescription ? errorDescription[0] : "",
              });
              if (errorDescription) {
                dispatchNotification({
                  code: "CFE-002",
                  message: errorDescription[0],
                  type: "addNotificationByCode",
                });
              } else {
                dispatchNotification({
                  code: "CFE-004",
                  type: "addNotificationByCode",
                });
              }
            });
        }
      })
      .catch(() => {
        // todo: handle error here
      });
  };

  const handleConfirmZipCodeModal = () => {
    setDataLayerTrack("Wrong Zip Code Modal Ignore Clicked");
    setShowZipCodeModal(false);
    handleSubmitAddress(postcodeWarningAddresses, true);
  };
  const handleDismissZipCodeModal = () => {
    setDataLayerTrack("Wrong Zip Code Modal Correct Clicked");
    setShowZipCodeModal(false);
  };

  return (
    <CartAddressStep
      countries={[]}
      disabled={disabled}
      onSubmitAddress={handleSubmitAddress}
      onConfirmZipCodeModal={handleConfirmZipCodeModal}
      onDismissZipCodeModal={handleDismissZipCodeModal}
      shippingOptions={cart.shippingOptions}
      showZipCodeModal={showZipCodeModal}
      postcode={addresses.delivery && addresses.delivery.postcode}
      user={user}
    />
  );
};

export default CartAddressStepContainer;
